/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Paper, Stack, TextField, Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useMutation, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { publicSignAPI } from '../api/crm'
import { getErrorMessage } from '../utils/apiErrors'
import Loading from '../components/common/Loading'
import PdfPreview from '../components/documents/PdfPreview'

const day = (iso) => format(new Date(iso), 'd MMM yyyy')

/** /sign#token=…: the person asked to sign reads the PDF, types their name, agrees and signs (or declines). No account needed. */
const SignPage = () => {
  // the token is in the #fragment, so it never reaches server logs
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '')
  const view = useQuery({ queryKey: ['sign', token], queryFn: () => publicSignAPI.open(token), enabled: !!token, retry: false })
  const pdf = useQuery({ queryKey: ['sign', token, 'pdf'], queryFn: () => publicSignAPI.document(token), enabled: !!view.data, retry: false })
  const [name, setName] = useState('')
  const [consent, setConsent] = useState(false)
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')
  const [result, setResult] = useState(null)
  const sign = useMutation({ mutationFn: () => publicSignAPI.sign(token, name.trim(), consent), onSuccess: setResult })
  const decline = useMutation({ mutationFn: () => publicSignAPI.decline(token, reason.trim() || undefined), onSuccess: (v) => { setDeclining(false); setResult(v) } })

  const frame = (children) => (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, md: 4 }, px: 2 }}>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>{children}</Box>
    </Box>
  )
  if (!token) return frame(<Alert severity="error">This signing link isn’t complete. Open it again from the email.</Alert>)
  if (view.isLoading) return <Loading fullScreen />
  if (view.error) return frame(<Alert severity="error">{getErrorMessage(view.error)}</Alert>)

  const v = result || view.data
  const closed = v.requestStatus !== 'SENT'
  const done = v.status === 'SIGNED'

  return frame(
    <Stack spacing={2}>
      <Box>
        <Typography variant="overline" color="text.secondary">{v.workspace}</Typography>
        <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>{v.documentName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {v.sender} asks {v.signerName} ({v.signerEmail}) to sign this document{v.otherSigners.length ? `, with ${v.otherSigners.join(', ')}` : ''}.
        </Typography>
      </Box>
      {v.message && <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap' }}><Typography variant="body2">{v.message}</Typography></Paper>}

      {done && (
        <Alert icon={<CheckCircleOutlineIcon />} severity="success">
          {v.requestStatus === 'COMPLETED'
            ? `Signed. Everyone has signed now${v.copyByEmail ? ': the signed copy is on its way to your email.' : '. Ask the sender for the signed copy.'}`
            : `Signed, thank you.${v.copyByEmail ? ' You’ll get the signed copy by email when everyone has signed.' : ''}`}
        </Alert>
      )}
      {!done && closed && (
        <Alert severity={v.status === 'DECLINED' ? 'info' : 'warning'}>
          {{ DECLINED: v.status === 'DECLINED' ? 'You declined to sign. The sender has been told.' : 'Someone declined to sign, so this request is closed.',
            CANCELLED: 'The sender cancelled this request.', EXPIRED: 'This request has expired; ask the sender for a new one.',
            COMPLETED: 'This document has been signed by everyone.' }[v.requestStatus]}
        </Alert>
      )}
      {!closed && v.status === 'WAITING' && <Alert severity="info">Someone signs before you. You can read the document now and sign once it’s your turn.</Alert>}

      {pdf.error ? <Alert severity="error">{getErrorMessage(pdf.error)}</Alert> : pdf.isLoading ? <Loading /> : <PdfPreview blob={pdf.data} height={640} title={v.documentName} />}

      {!closed && v.status === 'PENDING' && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }} component="form" onSubmit={(e) => { e.preventDefault(); sign.mutate() }}>
          <Stack spacing={2}>
            <Typography variant="h6">Sign</Typography>
            {sign.error && <Alert severity="error">{getErrorMessage(sign.error)}</Alert>}
            <TextField label="Type your full name" required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
              inputProps={{ maxLength: 100 }} helperText={`As ${v.signerName}`} />
            {name.trim().length > 1 && (
              <Typography sx={{ fontFamily: '"Brush Script MT", "Segoe Script", cursive', fontSize: 30, lineHeight: 1.2 }}>{name.trim()}</Typography>
            )}
            <FormControlLabel control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
              label="I have read this document and I agree to sign it electronically. My typed name is my signature." />
            <Typography variant="caption" color="text.secondary">
              We record your name, the time, your IP address and browser with the signature, and the document’s fingerprint
              ({v.documentSha256.slice(0, 16)}…) so it can be shown that this exact document was signed. Link valid until {day(v.expiresAt)}.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button color="inherit" onClick={() => setDeclining(true)}>Decline</Button>
              <Button type="submit" variant="contained" disabled={!consent || name.trim().length < 2 || sign.isPending}>
                {sign.isPending ? 'Signing…' : 'Sign document'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Dialog open={declining} onClose={() => setDeclining(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Decline to sign?</DialogTitle>
        <DialogContent>
          {decline.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(decline.error)}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>The sender is told, and the request closes for everyone.</Typography>
          <TextField fullWidth multiline minRows={2} label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 500 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclining(false)}>Back</Button>
          <Button color="error" variant="contained" disabled={decline.isPending} onClick={() => decline.mutate()}>Decline</Button>
        </DialogActions>
      </Dialog>
    </Stack>,
  )
}

export default SignPage
